import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { useSearchParams } from "react-router-dom";
import {
  Box, Typography, Button, Stack, Chip, LinearProgress,
} from "@mui/material";
import {
  WhatsApp, Share, ArrowForward, ContentCopy, Check,
  CheckCircle, Cancel, MedicalServices, Biotech, Psychology,
} from "@mui/icons-material";

const DOMAIN  = "https://drpawleb.com";
const DISPLAY = '"Cormorant Garamond", Georgia, serif';
const BLUE    = "#1565C0";
const BABY    = "#42A5F5";

type Stage = "reading" | "answering" | "result";

const DrPawLogo: React.FC = () => (
  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
    <Box sx={{
      width: 42, height: 42, borderRadius: "12px",
      background: `linear-gradient(135deg,${BLUE},${BABY})`,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 16px rgba(21,101,192,.25)",
    }}>
      <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "1.05rem" }}>D</Typography>
    </Box>
    <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: "1.65rem", letterSpacing: "-.01em", fontFamily: DISPLAY }}>
      Dr.Paw
    </Typography>
  </Stack>
);

const OPTION_LETTERS = ["A", "B", "C", "D"];

// ─────────────────────────────────────────────────────────────────────────────
const PublicCase: React.FC = () => {
  const [searchParams] = useSearchParams();
  const title       = decodeURIComponent(searchParams.get("title")       || "");
  const scenario    = decodeURIComponent(searchParams.get("scenario")    || "");
  const question    = decodeURIComponent(searchParams.get("question")    || "");
  const explanation = decodeURIComponent(searchParams.get("explanation") || "");
  const link        = searchParams.get("link") || `${DOMAIN}/#pricing`;
  const correctIdx  = parseInt(searchParams.get("answer") ?? "0", 10);

  const options = useMemo(() => {
    const result: string[] = [];
    (["a","b","c","d"] as const).forEach((k) => {
      const v = searchParams.get(k);
      if (v) result.push(decodeURIComponent(v));
    });
    return result;
  }, [searchParams]);

  const solvedCount = useMemo(() => {
    const h = scenario.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    return 280 + (h % 540);
  }, [scenario]);

  const [stage,    setStage]    = useState<Stage>("reading");
  const [selected, setSelected] = useState<number | null>(null);
  const [copied,   setCopied]   = useState(false);

  const isCorrect = selected === correctIdx;

  const handleSelect = (idx: number) => {
    if (stage !== "answering") return;
    setSelected(idx);
    setStage("result");
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShare = async () => {
    const resultText = selected !== null
      ? (isCorrect ? `I solved the Dr.Paw case! ✅ "${title}"` : `Tricky one! I got the Dr.Paw case wrong 😅 "${title}"`)
      : `Can you solve this clinical case? 🐾 "${title}"`;
    const data = { title: "Dr.Paw Case Challenge", text: `${resultText}\n\nTry it:`, url: window.location.href };
    if (typeof navigator.share === "function") { try { await navigator.share(data); return; } catch {} }
    handleCopy();
  };

  const waMsg = encodeURIComponent(
    selected !== null
      ? `I just ${isCorrect ? "solved ✅" : "attempted"} this clinical case on Dr.Paw! 🐾\n\n"${title}"\n\nCan you solve it? ${window.location.href}`
      : `Can you solve this clinical case? 🐾\n\n"${title}"\n\n${window.location.href}`
  );

  if (!title || !scenario || options.length < 2) return (
    <Box sx={{ minHeight: "100vh", background: "#F8F3EE", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ color: "#90A4AE" }}>Invalid or missing case link.</Typography>
    </Box>
  );

  const metaTitle = title ? `Case Study: ${title} — Dr.Paw` : "Veterinary Case Study — Dr.Paw";
  const metaDesc  = scenario
    ? `${scenario.slice(0, 120)} — Diagnose this case and test your clinical skills. Dr.Paw veterinary education.`
    : "Challenge your diagnostic skills with this Dr.Paw clinical case study. Share it with your colleagues!";

  return (
    <>
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content="https://drpawleb.com/og-image.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
    </Helmet>
    <HreflangTags />
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(160deg,#FEF9F5 0%,#F8F3EE 55%,#F2EDE6 100%)",
      display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
    }}>
      {/* Soft colour blobs */}
      <Box sx={{ position: "absolute", top: -120, right: -120, width: 480, height: 480, borderRadius: "50%", background: `radial-gradient(circle,rgba(21,101,192,.07) 0%,transparent 70%)`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 360, height: 360, borderRadius: "50%", background: `radial-gradient(circle,rgba(66,165,245,.08) 0%,transparent 70%)`, pointerEvents: "none" }} />

      {/* Header */}
      <Box sx={{ pt: 4, pb: 2.5, px: 3, textAlign: "center" }}>
        <DrPawLogo />
        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: BABY, fontSize: ".7rem", letterSpacing: ".15em", textTransform: "uppercase", fontWeight: 600 }}>
          Case of the Week
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 600 }}>

          {/* Progress strip */}
          <Stack direction="row" justifyContent="center" spacing={1} sx={{ mb: 3 }}>
            {(["reading","answering","result"] as Stage[]).map((s, i) => (
              <Box key={s} sx={{
                flex: 1, height: 4, borderRadius: 99,
                bgcolor: stage === s ? BLUE : i < ["reading","answering","result"].indexOf(stage) ? `rgba(21,101,192,.35)` : `rgba(21,101,192,.1)`,
                transition: "all .3s ease",
              }} />
            ))}
          </Stack>

          {/* ── READING STAGE ─────────────────────────────── */}
          {stage === "reading" && (
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                <Chip
                  icon={<MedicalServices sx={{ fontSize: ".9rem !important", color: `${BLUE} !important` }} />}
                  label="Clinical Case"
                  sx={{ bgcolor: `rgba(21,101,192,.1)`, color: BLUE, fontWeight: 700, border: `1px solid rgba(21,101,192,.22)` }}
                />
                <Chip
                  label={`${solvedCount.toLocaleString()}+ vets attempted`}
                  size="small"
                  sx={{ bgcolor: "rgba(34,197,94,.1)", color: "#059669", fontWeight: 600, fontSize: ".72rem", border: "1px solid rgba(34,197,94,.2)" }}
                />
              </Stack>

              <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "1.6rem", md: "2rem" }, lineHeight: 1.2, letterSpacing: "-.01em", mb: 3, fontFamily: DISPLAY }}>
                {title}
              </Typography>

              {/* Scenario card */}
              <Box sx={{ background: "#fff", border: `1px solid rgba(21,101,192,.15)`, borderRadius: "22px", p: { xs: 2.5, md: 3.5 }, mb: 3, position: "relative", boxShadow: "0 4px 24px rgba(21,101,192,.08)" }}>
                <Box sx={{ position: "absolute", left: 0, top: "15%", height: "70%", width: 3, background: `linear-gradient(180deg,transparent,${BLUE},transparent)`, borderRadius: "0 3px 3px 0" }} />
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: `rgba(21,101,192,.1)`, border: `1px solid rgba(21,101,192,.2)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Biotech sx={{ color: BLUE, fontSize: "1rem" }} />
                  </Box>
                  <Typography sx={{ color: BLUE, fontWeight: 700, fontSize: ".82rem", letterSpacing: ".08em", textTransform: "uppercase" }}>
                    Patient Presentation
                  </Typography>
                </Stack>
                <Typography sx={{ color: "#37474F", fontSize: { xs: ".92rem", md: "1rem" }, lineHeight: 1.85 }}>
                  {scenario}
                </Typography>
              </Box>

              <Button
                variant="contained" endIcon={<Psychology />} onClick={() => setStage("answering")} fullWidth size="large"
                sx={{ py: 1.7, borderRadius: "50px", textTransform: "none", fontWeight: 800, fontSize: "1rem", background: `linear-gradient(135deg,${BLUE},${BABY})`, boxShadow: "0 8px 28px rgba(21,101,192,.3)", "&:hover": { background: `linear-gradient(135deg,#0D47A1,${BLUE})`, transform: "translateY(-2px)" }, transition: "all .25s ease" }}
              >
                Ready to Diagnose
              </Button>
            </Box>
          )}

          {/* ── ANSWERING STAGE ───────────────────────────── */}
          {stage === "answering" && (
            <Box>
              <Box sx={{ background: "#fff", border: `1px solid rgba(21,101,192,.15)`, borderRadius: "20px", p: { xs: 2.5, md: 3 }, mb: 3, boxShadow: "0 2px 16px rgba(21,101,192,.07)" }}>
                <Chip label="Your diagnosis" size="small" sx={{ bgcolor: `rgba(21,101,192,.1)`, color: BLUE, fontWeight: 700, fontSize: ".72rem", mb: 1.5 }} />
                <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "1.1rem", md: "1.35rem" }, lineHeight: 1.35, fontFamily: DISPLAY }}>
                  {question}
                </Typography>
              </Box>

              <Stack spacing={1.5}>
                {options.map((opt, idx) => (
                  <Box
                    key={idx}
                    onClick={() => handleSelect(idx)}
                    sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: 1.8, md: 2.2 }, borderRadius: "14px", cursor: "pointer", background: "#fff", border: `1px solid rgba(21,101,192,.12)`, transition: "all .2s ease", boxShadow: "0 1px 8px rgba(21,101,192,.05)", "&:hover": { background: `rgba(21,101,192,.06)`, borderColor: `rgba(21,101,192,.35)`, transform: "translateX(6px)", boxShadow: "0 4px 16px rgba(21,101,192,.1)" } }}
                  >
                    <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: `rgba(21,101,192,.09)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ color: BLUE, fontWeight: 800, fontSize: ".85rem" }}>{OPTION_LETTERS[idx]}</Typography>
                    </Box>
                    <Typography sx={{ color: "#37474F", fontWeight: 500, fontSize: ".95rem", flex: 1, lineHeight: 1.4 }}>{opt}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* ── RESULT STAGE ──────────────────────────────── */}
          {stage === "result" && selected !== null && (
            <Box>
              {/* Result banner */}
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Box sx={{
                  width: 80, height: 80, borderRadius: "50%", mx: "auto", mb: 2,
                  background: isCorrect ? "radial-gradient(circle,rgba(34,197,94,.15) 0%,transparent 70%)" : "radial-gradient(circle,rgba(239,68,68,.15) 0%,transparent 70%)",
                  border: `2px solid ${isCorrect ? "rgba(34,197,94,.35)" : "rgba(239,68,68,.35)"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {isCorrect
                    ? <CheckCircle sx={{ color: "#22C55E", fontSize: 40 }} />
                    : <Cancel sx={{ color: "#EF4444", fontSize: 40 }} />}
                </Box>
                <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "1.6rem", md: "2rem" }, fontFamily: DISPLAY, letterSpacing: "-.01em", mb: 0.5 }}>
                  {isCorrect ? "Correct Diagnosis! ✅" : "Not quite — here's why:"}
                </Typography>
                {!isCorrect && (
                  <Typography sx={{ color: "#546E7A", fontSize: ".9rem" }}>
                    Correct answer: <strong style={{ color: "#059669" }}>{OPTION_LETTERS[correctIdx]}) {options[correctIdx]}</strong>
                  </Typography>
                )}
              </Box>

              {/* Answer recap */}
              <Stack spacing={1.2} sx={{ mb: 3 }}>
                {options.map((opt, idx) => {
                  const isChosen = idx === selected;
                  const isRight  = idx === correctIdx;
                  let bg = "#F8F9FB", border = "1px solid #E8EDF5", textClr = "#90A4AE", badgeBg = "#EEF2F7", badgeClr = "#90A4AE";
                  if (isRight)              { bg = "rgba(34,197,94,.07)";  border = "1px solid rgba(34,197,94,.3)";  textClr = "#065F46";  badgeBg = "rgba(34,197,94,.15)";  badgeClr = "#059669"; }
                  if (isChosen && !isRight) { bg = "rgba(239,68,68,.07)";  border = "1px solid rgba(239,68,68,.3)";  textClr = "#991B1B";  badgeBg = "rgba(239,68,68,.12)";  badgeClr = "#DC2626"; }
                  return (
                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: 1.6, md: 2 }, borderRadius: "13px", background: bg, border }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: "9px", bgcolor: badgeBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {isRight ? <CheckCircle sx={{ color: "#22C55E", fontSize: "1rem" }} /> : isChosen ? <Cancel sx={{ color: "#EF4444", fontSize: "1rem" }} /> : <Typography sx={{ color: badgeClr, fontWeight: 800, fontSize: ".78rem" }}>{OPTION_LETTERS[idx]}</Typography>}
                      </Box>
                      <Typography sx={{ color: textClr, fontWeight: isRight ? 700 : 400, fontSize: ".88rem", flex: 1, lineHeight: 1.35 }}>{opt}</Typography>
                    </Box>
                  );
                })}
              </Stack>

              {/* Clinical explanation */}
              {explanation && (
                <Box sx={{ background: `rgba(21,101,192,.05)`, border: `1px solid rgba(21,101,192,.18)`, borderRadius: "18px", p: { xs: 2.5, md: 3 }, mb: 3, boxShadow: "0 2px 16px rgba(21,101,192,.06)" }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: `rgba(21,101,192,.12)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <MedicalServices sx={{ color: BLUE, fontSize: "1rem" }} />
                    </Box>
                    <Typography sx={{ color: BLUE, fontWeight: 700, fontSize: ".85rem", letterSpacing: ".06em", textTransform: "uppercase" }}>Clinical Explanation</Typography>
                  </Stack>
                  <Typography sx={{ color: "#37474F", fontSize: ".9rem", lineHeight: 1.8 }}>{explanation}</Typography>
                </Box>
              )}

              {/* Instagram hook */}
              <Box sx={{ background: `rgba(66,165,245,.07)`, border: `1px solid rgba(66,165,245,.22)`, borderRadius: "12px", px: 2.5, py: 1.2, mb: 2.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8 }}>
                <Typography sx={{ fontSize: ".8rem" }}>📱</Typography>
                <Typography sx={{ color: "#546E7A", fontSize: ".8rem" }}>
                  Share your result and tag <strong style={{ color: BLUE }}>#DrPawCase</strong> — we'll reshare it!
                </Typography>
              </Box>

              {/* Sharing */}
              <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                <Button
                  variant="contained" startIcon={<Share />} onClick={handleShare} fullWidth
                  sx={{ py: 1.4, borderRadius: "50px", textTransform: "none", fontWeight: 700, background: "linear-gradient(135deg,#7C3AED,#A855F7)", boxShadow: "0 4px 14px rgba(124,58,237,.22)", "&:hover": { background: "linear-gradient(135deg,#6D28D9,#7C3AED)", transform: "translateY(-1px)" }, transition: "all .2s ease" }}
                >
                  Share My Result to Story
                </Button>
                <Button
                  variant="outlined" startIcon={<WhatsApp />} href={`https://wa.me/?text=${waMsg}`} target="_blank" fullWidth
                  sx={{ py: 1.3, borderRadius: "50px", textTransform: "none", fontWeight: 700, borderColor: "#25D366", color: "#25D366", bgcolor: "#fff", "&:hover": { bgcolor: "rgba(37,211,102,.06)" } }}
                >
                  Challenge a Colleague on WhatsApp
                </Button>
              </Stack>

              {/* CTA */}
              <Box sx={{ background: "#fff", border: `1px solid rgba(21,101,192,.18)`, borderRadius: "20px", p: { xs: 2.5, md: 3 }, mb: 2, boxShadow: "0 4px 20px rgba(21,101,192,.08)" }}>
                <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: ".95rem", mb: 1, fontFamily: DISPLAY }}>
                  Master cases like this one
                </Typography>
                <Typography sx={{ color: "#546E7A", fontSize: ".85rem", lineHeight: 1.75, mb: 2 }}>
                  Dr.Paw expert courses teach you to diagnose with confidence — real cases, clinical reasoning, expert guidance.
                </Typography>
                <Button
                  variant="contained" href={link} endIcon={<ArrowForward />} fullWidth
                  sx={{ py: 1.4, borderRadius: "50px", textTransform: "none", fontWeight: 800, background: `linear-gradient(135deg,${BLUE},${BABY})`, boxShadow: "0 6px 20px rgba(21,101,192,.25)", "&:hover": { background: `linear-gradient(135deg,#0D47A1,${BLUE})`, transform: "translateY(-2px)" }, transition: "all .2s ease" }}
                >
                  Explore Dr.Paw Courses
                </Button>
              </Box>

              <Button
                variant="outlined" startIcon={copied ? <Check /> : <ContentCopy />} onClick={handleCopy} fullWidth
                sx={{ py: 1.2, borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: copied ? "rgba(34,197,94,.4)" : `rgba(21,101,192,.25)`, color: copied ? "#22C55E" : "#78909C", bgcolor: "#fff", transition: "all .2s ease" }}
              >
                {copied ? "Link Copied!" : "Copy Case Link"}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ py: 3, textAlign: "center", borderTop: `1px solid rgba(21,101,192,.1)` }}>
        <Typography variant="caption" sx={{ color: "#B0BEC5", fontSize: ".72rem" }}>
          © Dr.Paw — Empowering Veterinary Professionals Worldwide
        </Typography>
      </Box>
    </Box>
    </>
  );
};

export default PublicCase;
